import React from "react";
import { cn } from "../lib/utils";

const FaqPanel = ({ showFAQ, setShowFAQ, activeTab, setActiveTab }) => {
  if (!showFAQ) return null;

  return (
    <div className="fixed inset-0 z-40" onClick={() => setShowFAQ(false)}>
      {/* Semi-transparent backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-20" />

      {/* FAQ Panel near the button */}
      <div
        className="absolute bottom-20 right-10 min-w-[600px] bg-white dark:bg-neutral-800 rounded-lg max-w-sm w-full shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Inner container: scrollable area with padding */}
        <div className="p-4 md:p-6 max-h-[80vh] overflow-y-auto custom-scrollbar">
          {/* Tab Buttons */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
            <button
              className={cn(
                "py-2 px-4 text-sm font-semibold transition-colors",
                activeTab === "faq"
                  ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400"
                  : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
              )}
              onClick={() => setActiveTab("faq")}
            >
              FAQs
            </button>
            <button
              className={cn(
                "py-2 px-4 text-sm font-semibold transition-colors",
                activeTab === "guidelines"
                  ? "border-b-2 border-blue-600 text-blue-600 dark:text-blue-400"
                  : "text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
              )}
              onClick={() => setActiveTab("guidelines")}
            >
              Guidelines
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "faq" && <FaqContent />}
          {activeTab === "guidelines" && <GuidelinesContent />}
        </div>
      </div>
    </div>
  );
};

const FaqContent = () => (
  <div className="text-gray-700 dark:text-gray-100">
    <h2 className="text-lg font-bold mb-3 text-gray-900 dark:text-gray-100">
      FAQs
    </h2>
    <ol className="list-decimal list-inside text-sm space-y-3">
      <li>
        <strong>What is CoachAI designed for?</strong> <br />
        CoachAI is built to assist with specific scenarios, ensuring
        structured and guided conversations.
      </li>
      <li>
        <strong>How does CoachAI's guardrails work?</strong> <br />
        CoachAI operates within a set of predefined guidelines, limiting
        responses to approved topics and preventing off-topic or unauthorized
        discussions.
      </li>
      <li>
        <strong>Can I ask CoachAI about any topic?</strong> <br />
        Yes, as long as the topic is on the leadership development. If you ask
        something outside its scope, it will politely redirect you.
      </li>
      <li>
        <strong>How do I know what topics CoachAI covers?</strong> <br />
        CoachAI provides a welcome message with a list of supported topics.
        You can also type "What can you help with?" to see its capabilities.
      </li>
      <li>
        <strong>Is my data safe while using CoachAI?</strong> <br />
        Yes, CoachAI follows security protocols and does not store sensitive
        or personal information and follows the current LLM guidelines.
      </li>
      <li>
        <strong>What happens if I ask something outside CoachAI's scope?</strong>{" "}
        <br />
        CoachAI will either redirect you to a relevant topic or inform you
        that it cannot process the request.
      </li>
      <li>
        <strong>CoachAI isn't responding correctly. What should I do?</strong>{" "}
        <br />
        Try rephrasing your query within CoachAI's supported topics. If the
        issue persists, please provide feedback by clicking "Give Feedback"
      </li>
    </ol>
  </div>
);

const GuidelinesContent = () => (
  <div className="text-gray-700 dark:text-gray-100">
    <h2 className="text-lg font-bold mb-3 text-gray-900 dark:text-gray-100">
      Guidelines
    </h2>
    <ul className="list-disc list-inside text-sm space-y-3">
      <li>
        CoachAI is designed to assist with team-related queries, workflow
        guidance, and scenario-based support. Use it for relevant work-related
        discussions.
      </li>
      <li>For accurate responses, phrase your questions clearly and concisely.</li>
      <li>
        Follow the CoachAI's instructions when prompted for specific inputs to
        ensure a smooth interaction.
      </li>
      <li>
        CoachAI does not handle off-topic or non-work-related queries, so
        keep interactions focused on its intended use.
      </li>
      <li>
        Avoid sharing sensitive or confidential information, as CoachAI is
        not designed to process or store such data.
      </li>
      <li>
        While CoachAI provides guidance, always verify critical information
        before making decisions based on its responses.
      </li>
      <li>
        To get the most relevant answers, avoid vague or ambiguous queries. Be
        specific about what you need assistance with.
      </li>
    </ul>
  </div>
);

export default FaqPanel;
